import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  Chip,
  alpha,
  LinearProgress,
  Tooltip,
  Badge,
  Alert,
} from '@mui/material';
import {
  TouchApp as TouchAppIcon,
  CheckCircle as CheckCircleIcon,
  Timer as TimerIcon,
  PersonOff as PersonOffIcon,
  Refresh as RefreshIcon,
  VolumeUp as VolumeUpIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Speed as SpeedIcon,
  CloudDownload as CloudDownloadIcon,
  PlaylistAdd as PlaylistAddIcon,
} from '@mui/icons-material';
import useWebSocketData from '../../hooks/useWebSocketData';
import SyncStatusIndicator from './SyncStatusIndicator';

const SenhaAtualPanel = ({
  senhaChamada,
  atendimentoIniciado,
  atendimentoFinalizado,
  tempoAtendimento,
  rechamando,
  formatarTempo,
  handleIniciarAtendimento,
  handleFinalizarAtendimento,
  handleMostrarDialogoNaoCompareceu,
  handleReabrirSenha,
  handleRechamarSenha,
  handleAdicionarServicos,
  customTheme,
  // Props do aviso de aguardo na fila
  aguardandoNaFila,
  mensagemAguardo,
}) => {
  // ==========================================
  // INTEGRAÇÃO WEBSOCKET REAL-TIME V2.0
  // ==========================================
  
  // Hook WebSocket para indicadores de conexão (AGORA COM SISTEMA DE SINCRONIZAÇÃO ROBUSTO!)
  const {
    conectado,
    carregando,
    erro,
    ultimaAtualizacao,
    // NOVOS: Estados de sincronização
    syncStatus,
    lastSyncTime,
    retryCount,
    networkQuality,
    cacheSize,
    isOnline,
    isOffline,
    isSyncing,
    hasRecentData,
    // NOVOS: Funções de controle de sincronização
    forceSync,
    clearCache,
    attemptReconnection
  } = useWebSocketData({
    senhas: true,
    fila: false,
    estatisticas: false,
    usuario: false
  });
  
  // Estado para animação do indicador de conexão
  const [pulseAnimation, setPulseAnimation] = useState(false);
  
  // Estado para o tempo de espera atualizado em tempo real
  const [tempoEsperaAtualizado, setTempoEsperaAtualizado] = useState(senhaChamada?.tempEspera || '0 min');
  
  // Função para calcular o tempo de espera em tempo real
  const calcularTempoEspera = useCallback((dataEmissao) => {
    if (!dataEmissao) return '0 min';
    
    const agora = new Date();
    const emissao = new Date(dataEmissao);
    const diferencaMs = agora - emissao;
    const diferencaMinutos = Math.floor(diferencaMs / (1000 * 60));
    
    if (diferencaMinutos < 60) {
      return `${diferencaMinutos} min`;
    } else {
      const horas = Math.floor(diferencaMinutos / 60);
      const minutosRestantes = diferencaMinutos % 60;
      return `${horas}h ${minutosRestantes}min`;
    }
  }, []);
  
  // Efeito para atualizar o tempo de espera em tempo real
  useEffect(() => {
    if (!senhaChamada?.emitida_em) {
      setTempoEsperaAtualizado('0 min');
      return;
    }

    // Atualização inicial
    setTempoEsperaAtualizado(calcularTempoEspera(senhaChamada.emitida_em));

    // Configurar intervalo para atualização a cada 30 segundos
    const intervalId = setInterval(() => {
      setTempoEsperaAtualizado(calcularTempoEspera(senhaChamada.emitida_em));
    }, 30000);

    // Limpeza do intervalo
    return () => {
      clearInterval(intervalId);
    };
  }, [senhaChamada?.emitida_em, calcularTempoEspera]);
  
  // Efeito para animação quando há atualizações
  useEffect(() => {
    if (ultimaAtualizacao) {
      setPulseAnimation(true);
      const timer = setTimeout(() => setPulseAnimation(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [ultimaAtualizacao]);

  // ==========================================
  // INDICADOR DE CONEXÃO WEBSOCKET V2.0
  // ==========================================
  
  const renderWebSocketIndicator = () => (
    <SyncStatusIndicator
      syncStatus={syncStatus}
      lastSyncTime={lastSyncTime}
      retryCount={retryCount}
      networkQuality={networkQuality}
      cacheSize={cacheSize}
      isOnline={isOnline}
      isOffline={isOffline}
      isSyncing={isSyncing}
      hasRecentData={hasRecentData}
      onForceSync={forceSync}
      onClearCache={clearCache}
      onReconnect={attemptReconnection}
      compact={true}
      customTheme={customTheme}
    />
  );

  // ==========================================
  // INDICADOR LEGADO (COMPATIBILIDADE)
  // ==========================================
  
  const renderLegacyWebSocketIndicator = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip 
        title={
          isOnline 
            ? `Sistema sincronizado • Última atualização: ${ultimaAtualizacao ? new Date(ultimaAtualizacao).toLocaleTimeString() : 'N/A'} • Cache: ${Math.round(cacheSize/1024)}KB`
            : erro 
              ? `Erro de conexão: ${erro} • Usando cache local`
              : 'Conectando... Aguarde'
        }
      >
        <Badge
          variant="dot"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: isOnline ? '#44b700' : erro ? '#f44336' : '#ff9800',
              color: isOnline ? '#44b700' : erro ? '#f44336' : '#ff9800',
              animation: carregando || pulseAnimation ? 'pulse 1.5s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { transform: 'scale(0.95)', opacity: 1 },
                '70%': { transform: 'scale(1)', opacity: 0.7 },
                '100%': { transform: 'scale(0.95)', opacity: 1 }
              }
            }
          }}
        >
        </Badge>
      </Tooltip>
      
      {/* Indicador de tempo real com status de sincronização */}
      <Typography 
        variant="caption" 
        sx={{ 
          color: isOnline ? 'success.main' : 'text.secondary',
          fontWeight: 500,
          fontSize: '10px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}
      >
        {isOnline ? 'Real-time' : isOffline && hasRecentData ? 'Cache' : 'Offline'}
      </Typography>
      
      {/* Indicador de retry quando necessário */}
      {retryCount > 0 && (
        <Chip
          size="small"
          label={`${retryCount}x`}
          color="warning"
          variant="outlined"
          sx={{ 
            height: 16,
            fontSize: '8px',
            '& .MuiChip-label': {
              px: 0.5
            }
          }}
        />
      )}
    </Box>
  );

  // Se não tem senha chamada, exibe mensagem
  if (!senhaChamada) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          textAlign: 'center',
          height: '100%',
          maxHeight: { md: '500px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          position: 'relative'
        }}
      >
        {/* Indicador WebSocket V2.0 no canto superior direito */}
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          {renderWebSocketIndicator()}
        </Box>
        
        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
          Nenhuma senha em atendimento
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          {isOnline 
            ? 'Chame a próxima senha para iniciar o atendimento'
            : hasRecentData
              ? 'Funcionando offline com dados do cache • Aguardando reconexão...'
              : 'Sem conexão e sem dados em cache • Tente forçar sincronização'
          }
        </Typography>
        
        {/* Botões de ação quando offline */}
        {isOffline && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={attemptReconnection}
              disabled={isSyncing}
            >
              Reconectar
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudDownloadIcon />}
              onClick={forceSync}
              disabled={isSyncing}
            >
              Sincronizar
            </Button>
          </Box>
        )}
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        height: '100%',
        maxHeight: { md: '500px' },
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      <Box
        sx={{
          p: 2,
          bgcolor: senha => {
            // Status/cor baseado na situação atual
            if (atendimentoFinalizado) return alpha(customTheme.palette.success.main, 0.1);
            if (atendimentoIniciado) return alpha(customTheme.palette.info.main, 0.1); 
            return alpha(customTheme.palette.warning.main, 0.1);
          },
          borderBottom: '1px solid',
          borderColor: senha => {
            if (atendimentoFinalizado) return alpha(customTheme.palette.success.main, 0.2);
            if (atendimentoIniciado) return alpha(customTheme.palette.info.main, 0.2);
            return alpha(customTheme.palette.warning.main, 0.2);
          },
          color: senha => {
            if (atendimentoFinalizado) return 'success.main';
            if (atendimentoIniciado) return 'info.main';
            return 'warning.main';
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {atendimentoFinalizado ? (
            <CheckCircleIcon sx={{ mr: 1 }} />
          ) : atendimentoIniciado ? (
            <TimerIcon sx={{ mr: 1 }} />
          ) : (
            <TouchAppIcon sx={{ mr: 1 }} />
          )}
          <Typography variant="subtitle1" fontWeight={600}>
            {atendimentoFinalizado
              ? 'Atendimento Finalizado'
              : atendimentoIniciado
                ? 'Em Atendimento'
                : 'Aguardando Início'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={formatarTempo(tempoAtendimento)} 
            size="small" 
            icon={<TimerIcon fontSize="small" />}
            sx={{ 
              bgcolor: 'background.paper', 
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }} 
          />
          {renderWebSocketIndicator()}
        </Box>
      </Box>

      <Box 
        sx={{ 
          p: 3, 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          animation: rechamando ? 'pulse 1s infinite' : 'none',
          '@keyframes pulse': {
            '0%': {
              backgroundColor: alpha(customTheme.palette.primary.main, 0)
            },
            '50%': {
              backgroundColor: alpha(customTheme.palette.primary.main, 0.1)
            },
            '100%': {
              backgroundColor: alpha(customTheme.palette.primary.main, 0)
            }
          }
        }}
      >
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" component="div" sx={{ mr: 2 }}>Senha:</Typography>
          <Typography 
            variant="h3" 
            component="div" 
            sx={{ 
              fontWeight: 'bold',
              color: 'primary.main',
              flex: 1
            }}
          >
            {senhaChamada.numero}
            
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'inline-block',
                ml: 1,
                color: 'text.secondary',
                fontSize: '30%',
                textTransform: 'uppercase',
                verticalAlign: 'middle'
              }}
            >
              • {senhaChamada.tipo}
            </Typography>
          </Typography>
          
          {senhaChamada.preferencial && (
            <Chip 
              label="Preferencial" 
              size="small" 
              color="secondary"
              sx={{ ml: 1 }} 
            />
          )}
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
            Tempo de Espera: <strong>{tempoEsperaAtualizado}</strong>
          </Typography>
          
          {/* Exibir serviços adicionais se existirem */}
          {senhaChamada.servicos_adicionais && senhaChamada.servicos_adicionais.length > 0 && (
            <Box sx={{ mt: 1, mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                Serviços Adicionais:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {senhaChamada.servicos_adicionais.map((servico, index) => (
                  <Chip
                    key={index}
                    label={servico}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* NOVO: Indicador de fonte dos dados */}
          {isOffline && hasRecentData && (
            <Typography variant="caption" sx={{ color: 'warning.main', fontStyle: 'italic' }}>
              ⚠️ Dados do cache local • Última sincronização: {lastSyncTime ? new Date(lastSyncTime).toLocaleTimeString() : 'N/A'}
            </Typography>
          )}
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={atendimentoFinalizado ? 100 : atendimentoIniciado ? 60 : 30} 
          sx={{ 
            mb: 3, 
            height: 6, 
            borderRadius: 3,
            bgcolor: alpha(customTheme.palette.grey[500], 0.1),
            '& .MuiLinearProgress-bar': {
              bgcolor: atendimentoFinalizado 
                ? customTheme.palette.success.main 
                : atendimentoIniciado 
                  ? customTheme.palette.info.main 
                  : customTheme.palette.warning.main,
              borderRadius: 3,
            }
          }}
        />
        
        {/* Aviso de aguardo na fila */}
        {aguardandoNaFila && mensagemAguardo && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              '& .MuiAlert-message': {
                fontSize: '14px',
                fontWeight: 500
              },
              '& .MuiAlert-icon': {
                fontSize: '20px'
              }
            }}
          >
            {mensagemAguardo}
          </Alert>
        )}
        
        <Box 
          sx={{ 
            mt: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 1 },
            width: '100%'
          }}
        >
          {/* Condicional para mostrar apenas os botões relevantes */}
          {!atendimentoIniciado && !atendimentoFinalizado && (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<TouchAppIcon />}
                onClick={handleIniciarAtendimento}
                disabled={isOffline && !hasRecentData} // Desabilitar se offline sem dados
                sx={{
                  py: { xs: 1.5, sm: 1.2 },
                  px: { xs: 2, sm: 1.5 },
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.8125rem', md: '0.875rem' },
                  minHeight: { xs: '48px', sm: '52px' },
                  flex: 1,
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1.2,
                  boxShadow: `0 4px 14px ${alpha(customTheme.palette.primary.main, 0.4)}`,
                  '& .MuiButton-startIcon': {
                    marginLeft: 0,
                    marginRight: { xs: 1, sm: 0.5 }
                  }
                }}
              >
                Iniciar
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<PersonOffIcon />}
                onClick={handleMostrarDialogoNaoCompareceu}
                disabled={isOffline && !hasRecentData}
                sx={{
                  py: { xs: 1.5, sm: 1.2 },
                  px: { xs: 2, sm: 1 },
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.75rem', md: '0.8125rem' },
                  minHeight: { xs: '48px', sm: '52px' },
                  flex: 1,
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1.2,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2
                  },
                  '& .MuiButton-startIcon': {
                    marginLeft: 0,
                    marginRight: { xs: 1, sm: 0.25 }
                  }
                }}
              >
                Não Compareceu
              </Button>
              <Box sx={{ flex: 1 }}>
                <ReclaimButtonWithTimer
                  onClick={handleRechamarSenha}
                  disabled={isOffline && !hasRecentData}
                  customTheme={customTheme}
                />
              </Box>
            </>
          )}
          
          {atendimentoIniciado && !atendimentoFinalizado && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleFinalizarAtendimento}
                disabled={isOffline && !hasRecentData}
                sx={{
                  py: { xs: 1.5, sm: 1.2 },
                  px: { xs: 2, sm: 1.5 },
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.8125rem', md: '0.875rem' },
                  minHeight: { xs: '48px', sm: '52px' },
                  flex: 1,
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1.2,
                  boxShadow: `0 4px 14px ${alpha(customTheme.palette.success.main, 0.4)}`,
                  '& .MuiButton-startIcon': {
                    marginLeft: 0,
                    marginRight: { xs: 1, sm: 0.5 }
                  }
                }}
              >
                Finalizar
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<PlaylistAddIcon />}
                onClick={handleAdicionarServicos}
                disabled={isOffline && !hasRecentData}
                sx={{
                  py: { xs: 1.5, sm: 1.2 },
                  px: { xs: 2, sm: 1 },
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.75rem', md: '0.8125rem' },
                  minHeight: { xs: '48px', sm: '52px' },
                  flex: 1,
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1.2,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2
                  },
                  '& .MuiButton-startIcon': {
                    marginLeft: 0,
                    marginRight: { xs: 1, sm: 0.25 }
                  }
                }}
              >
                + Serviços
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<PersonOffIcon />}
                onClick={handleMostrarDialogoNaoCompareceu}
                disabled={isOffline && !hasRecentData}
                sx={{
                  py: { xs: 1.5, sm: 1.2 },
                  px: { xs: 2, sm: 1 },
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.75rem', md: '0.8125rem' },
                  minHeight: { xs: '48px', sm: '52px' },
                  flex: 1,
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1.2,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2
                  },
                  '& .MuiButton-startIcon': {
                    marginLeft: 0,
                    marginRight: { xs: 1, sm: 0.25 }
                  }
                }}
              >
                Não Compareceu
              </Button>
            </>
          )}
          
          {atendimentoFinalizado && (
            <>
              <Tooltip title="Adicionar Serviços ao Atendimento">
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PlaylistAddIcon />}
                  onClick={handleAdicionarServicos}
                  disabled={isOffline && !hasRecentData}
                  sx={{
                    py: { xs: 1.5, sm: 1.2 },
                    px: { xs: 2, sm: 1.5 },
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '0.8125rem', md: '0.875rem' },
                    minHeight: { xs: '48px', sm: '52px' },
                    width: '100%',
                    textTransform: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1.2,
                    background: `linear-gradient(45deg, ${customTheme.palette.secondary.main}, ${customTheme.palette.secondary.dark})`,
                    boxShadow: `0 4px 14px ${alpha(customTheme.palette.secondary.main, 0.4)}`,
                    '&:hover': {
                      background: `linear-gradient(45deg, ${customTheme.palette.secondary.dark}, ${customTheme.palette.secondary.main})`,
                      boxShadow: `0 6px 18px ${alpha(customTheme.palette.secondary.main, 0.5)}`,
                    },
                    '& .MuiButton-startIcon': {
                      marginLeft: 0,
                      marginRight: { xs: 1, sm: 0.5 }
                    }
                  }}
                >
                  Adicionar Serviços
                </Button>
              </Tooltip>
              
              <Tooltip title="Reabrir Senha">
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={handleReabrirSenha}
                  disabled={isOffline && !hasRecentData}
                  sx={{
                    py: { xs: 1.5, sm: 1.2 },
                    px: { xs: 2, sm: 1.5 },
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '0.8125rem', md: '0.875rem' },
                    minHeight: { xs: '48px', sm: '52px' },
                    width: '100%',
                    textTransform: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1.2,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2
                    },
                    '& .MuiButton-startIcon': {
                      marginLeft: 0,
                      marginRight: { xs: 1, sm: 0.5 }
                    }
                  }}
                >
                  Reabrir Senha
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
        
        {/* NOVO: Aviso sobre funcionalidade offline */}
        {isOffline && !hasRecentData && (
          <Alert 
            severity="warning" 
            sx={{ 
              mt: 2,
              '& .MuiAlert-message': {
                fontSize: '12px'
              }
            }}
          >
            Sistema offline sem dados recentes. Funcionalidade limitada até reconectar.
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

// Componente para o botão de rechamar com timer elegante
const ReclaimButtonWithTimer = ({ onClick, disabled, customTheme, ...props }) => {
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let interval = null;
    
    if (isTimerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(time => {
          const newTime = time - 1;
          setProgress(((30 - newTime) / 30) * 100);
          
          if (newTime <= 0) {
            setIsTimerActive(false);
            setProgress(0);
            return 30;
          }
          
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, timeRemaining]);

  const handleClick = () => {
    if (!isTimerActive && !disabled) {
      onClick();
      setIsTimerActive(true);
      setTimeRemaining(30);
      setProgress(0);
    }
  };

  const isButtonDisabled = disabled || isTimerActive;

  return (
    <Tooltip title={isTimerActive ? "Aguarde para rechamar novamente" : "Rechamar Senha"}>
      <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 2, width: '100%' }}>
        <Button
          variant="outlined"
          color="info"
          startIcon={<VolumeUpIcon />}
          onClick={handleClick}
          disabled={isButtonDisabled}
          sx={{
            py: { xs: 1.5, sm: 1.2 },
            px: { xs: 2, sm: 1 },
            borderRadius: 2,
            fontWeight: 600,
            fontSize: { xs: '0.875rem', sm: '0.75rem', md: '0.8125rem' },
            minHeight: { xs: '48px', sm: '52px' },
            borderWidth: 2,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            textTransform: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1.2,
            '&:hover': {
              borderWidth: 2
            },
            '&.Mui-disabled': {
              borderWidth: 2,
              borderColor: alpha(customTheme.palette.info.main, 0.5),
              color: alpha(customTheme.palette.info.main, 0.7),
              backgroundColor: 'transparent'
            },
            '& .MuiButton-startIcon': {
              marginLeft: 0,
              marginRight: { xs: 1, sm: 0.25 }
            }
          }}
          {...props}
        >
          {/* Barra de progresso que preenche o botão */}
          {isTimerActive && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: `${progress}%`,
                backgroundColor: alpha(customTheme.palette.info.main, 0.15),
                transition: 'width 1s linear',
                zIndex: 0
              }}
            />
          )}
          
          {/* Conteúdo do botão */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Rechamar
            </Box>
            
            {/* Timer no canto superior direito */}
            {isTimerActive && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -18,
                  right: -11,
                  backgroundColor: customTheme.palette.info.main,
                  color: 'white',
                  borderRadius: '50%',
                  minWidth: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  boxShadow: `0 2px 4px ${alpha(customTheme.palette.info.main, 0.4)}`,
                  zIndex: 1000
                }}
              >
                {timeRemaining}
              </Box>
            )}
          </Box>
        </Button>
      </Box>
    </Tooltip>
  );
};

export default SenhaAtualPanel; 